import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../../theming/useTheme';
import { DecorationLine } from '../../DecorationLine';
import { CardComponentDataProps } from '../../cards/dividedCard/DividedCard';
import { ListItem } from '../listItem/ListItem';
import { LIST_STYLE, LIST_WRAPPER_STYLE } from '../lists.const';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';

const componentConfigurationKey = 'list';
const createStyles = (
  backgroundColor: string,
  borderWidth: number,
  borderRadius: number,
  borderColor: string,
) => StyleSheet.create({
  listWrapper: {
    ...LIST_WRAPPER_STYLE,
    backgroundColor,
    border: `${borderWidth}px solid ${borderColor}`,
    borderRadius: `${borderRadius}px`,
    overflow: 'hidden',
  },
  list: {
    ...LIST_STYLE,
    [MEDIA_BREAKPOINTS.SMALL]: {
      position: 'relative',
    },
  },
});

interface ListComponentDataProps {
  listProps: CardComponentDataProps[],
}

interface ListComponentThemeProps {
  backgroundColor: string
  borderRadius: number
  borderWidth: number
  borderColor: string,
  boxShadowTypeNormal: string
  boxShadowTypeHover: string
  boxShadowTypeActive: string
}

type ListComponentProps = ListComponentDataProps & ListComponentThemeProps;

export const ListComponent: React.FunctionComponent<ListComponentProps> = (
  {
    listProps,
    backgroundColor,
    borderWidth,
    borderRadius,
    borderColor,
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
  },
) => {
  const styles = createStyles(
    backgroundColor,
    borderWidth,
    borderRadius,
    borderColor,
  );
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive });
  return (
    <div className={css(
      boxShadowStyle.style,
      styles.listWrapper,
    )}
    >
      <DecorationLine />
      <div className={css(styles.list)}>
        {listProps.map((listItemProps, index) => (<ListItem {...listItemProps} key={index} />))}
      </div>
    </div>
  );
};

ListComponent.defaultProps = {
  listProps: [],
};

const mapThemeToProps = (theme: ListComponentThemeProps) => ({
  backgroundColor: theme.backgroundColor,
  borderRadius: theme.borderRadius,
  borderWidth: theme.borderWidth,
  borderColor: theme.borderColor,
  boxShadowTypeNormal: theme.boxShadowTypeNormal,
  boxShadowTypeHover: theme.boxShadowTypeHover,
  boxShadowTypeActive: theme.boxShadowTypeActive,
});

export const List = (props: ListComponentDataProps) => {
  const themeContext = useTheme(mapThemeToProps, componentConfigurationKey);
  return <ListComponent {...themeContext} {...props} />;
};
